import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BUSINESS_INFO } from "../config";
import { SEO } from "../components/SEO";
import { CampaignBanner } from "../components/CampaignBanner";
import { OpeningOfferModal } from "../components/OpeningOfferModal";
import { GallerySection } from "../components/GallerySection";
import { ContactForm } from "../components/ContactForm";
import { WaveDivider } from "../components/WaveDivider";
import { getOptimizedImageUrl } from "../lib/utils";
import { useCmsCollection } from "../hooks/useCms";

export function HomePage() {
  const { items: ydelser } = useCmsCollection("ydelser", { sort: "sort_order" });
  const { items: cmsAnimeredeBilleder } = useCmsCollection("animerede_forside_billeder", {
    sort: "sort_order",
    order: "asc",
  });

  // Standard billeder til animeret hero-rulle som fallback
  const defaultReelCol1 = [
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003489.jpg",
      alt: "Uddannet hundefrisør Trine ved trimmebord med diplom og hund",
      aspect: "aspect-[3/4]",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003479.jpg",
      alt: "Lys Golden Retriever på det blå trimmebord i salonen",
      aspect: "aspect-square",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003464.jpg",
      alt: "Shetland Sheepdog der sidder roligt på salonens gulv",
      aspect: "aspect-[4/5]",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003452.jpg",
      alt: "Langhåret collie på trimmebord med strigler og børster",
      aspect: "aspect-[3/4]",
    },
  ];

  const defaultReelCol2 = [
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003461.jpg",
      alt: "Trine i salonen med collie under pelspleje",
      aspect: "aspect-[4/5]",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003484.jpg",
      alt: "Lille nyklippet hvid hund på det blå trimmebord",
      aspect: "aspect-[3/4]",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003482.jpg",
      alt: "Lille nyklippet hund på hæve-sænkebord i salonen",
      aspect: "aspect-square",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003451.jpg",
      alt: "Hundepels og pleje på trimmebord",
      aspect: "aspect-[4/5]",
    },
  ];

  // Henter billeder fra CMS samlingen "animerede_forside_billeder"
  const cmsCol1 = [];
  const cmsCol2 = [];

  if (cmsAnimeredeBilleder && cmsAnimeredeBilleder.length > 0) {
    cmsAnimeredeBilleder.forEach((item) => {
      const url = Array.isArray(item.image) ? item.image[0] : item.image;
      if (!url) return;
      const formatted = {
        url,
        alt: item.alt_text || item.title || "Hund hos Hundens Pels og Pleje",
        aspect: item.aspect_ratio || "aspect-[3/4]",
      };
      if (Number(item.column) === 2) {
        cmsCol2.push(formatted);
      } else if (Number(item.column) === 1) {
        cmsCol1.push(formatted);
      } else {
        // Hvis kolonne ikke er angivet, fordeles de skiftevis
        if (cmsCol1.length <= cmsCol2.length) {
          cmsCol1.push(formatted);
        } else {
          cmsCol2.push(formatted);
        }
      }
    });
  }

  const baseCol1 = cmsCol1.length > 0 ? cmsCol1 : defaultReelCol1;
  const baseCol2 = cmsCol2.length > 0 ? cmsCol2 : defaultReelCol2;

  // Duplikeret til uendeligt loop
  const reelCol1 = [...baseCol1, ...baseCol1];
  const reelCol2 = [...baseCol2, ...baseCol2];

  // Autentiske salon-billeder til visning
  const salonImages = [
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003489.jpg",
      alt: "Hundefrisør Trine ved trimmebord med lille hund, diplom og blomster",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003479.jpg",
      alt: "Lys Golden Retriever på et blåt trimmebord i salonen",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003464.jpg",
      alt: "Trefarvet Shetland Sheepdog der sidder på gulvet i hundesalonen",
    },
    {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003484.jpg",
      alt: "Nyklippet lille hvid hund stående på et blåt trimmebord i salonen",
    },
  ];

  const [slideOffset, setSlideOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setSlideOffset((prev) => (prev + 1) % salonImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPaused, salonImages.length]);

  const prevSlide = () => {
    setSlideOffset((prev) => (prev - 1 + salonImages.length) % salonImages.length);
  };

  const nextSlide = () => {
    setSlideOffset((prev) => (prev + 1) % salonImages.length);
  };

  const featuredServices = [
    {
      title: "Klip, vask, føn samt negle",
      size: "Alle størrelser (1-40 kg)",
      price: "Fra 760,00 kr.",
      time: "3 timer",
      desc: "Bad, skånsom føn, klip efter race og ønsker samt negle- og potepleje.",
      icon: "fa-scissors",
      link: "/ydelser-priser",
    },
    {
      title: "Vask & føn",
      size: "Lille, mellem & stor hund",
      price: "Fra 400,00 kr.",
      time: "1,5 - 3 timer",
      desc: "Grundig og skånsom vask med økologiske plejeprodukter tilpasset hunden, blid massage og føn.",
      icon: "fa-shower",
      link: "/ydelser-priser",
    },
    {
      title: "Hvalpepakke",
      size: "Hvalpe",
      price: "1.600,00 kr.",
      time: "3 rolige besøg",
      desc: "3-trins tryghedsforløb med mild hvalpeshampoo: øv på klippebord, vask & føn samt komplet klip.",
      icon: "fa-heart",
      link: "/hvalpepakke",
    },
  ];

  return (
    <>
      <SEO
        title="Hundesalon | Hundens Pels og Pleje | Fredericia"
        description="Kærlig og professionel hundepleje hos Hundens Pels og Pleje i Fredericia. Book klip, vask, føn, negle og trygge behandlinger med hundens behov i centrum."
        ogImage=""
      />

      {/* FULL WIDTH HERO WITH CENTERED TEXT - NO BOX BEHIND TEXT */}
      <section className="relative overflow-hidden bg-[#FEFAF3] min-h-[460px] sm:min-h-[640px] lg:min-h-[780px] flex flex-col justify-end items-center pb-8 sm:pb-16 lg:pb-20">
        {/* Full-width panoramic background image with slow subtle zoom */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={getOptimizedImageUrl(
              "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/hero-woman-blue-tshirt-hand-holding-paw.jpg",
              { width: 1920, quality: 85 }
            )}
            alt="Kvinde i blå t-shirt der nænsomt holder en hundepote i Hundens Pels og Pleje"
            className="h-full w-full object-cover object-center animate-hero-zoom"
            loading="eager"
          />
          {/* Lighter subtle overlay so the image stays luminous while text remains readable */}
          <div className="absolute inset-0 bg-black/25" />
          <div className="absolute inset-0 bg-radial from-black/10 via-black/20 to-black/35" />
        </div>

        {/* Hero Content Container - Clean & open without box */}
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-16 pb-14 sm:pt-32 sm:pb-24 lg:pt-36 lg:pb-28 w-full text-center space-y-3.5 sm:space-y-5 animate-hero-fade-up">
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-tight drop-shadow-md mx-auto max-w-4xl">
            Hundesalon i Fredericia – Klip, vask & kærlig pelspleje
          </h1>

          <p className="max-w-xl mx-auto text-sm sm:text-base lg:text-lg text-neutral-100 leading-relaxed font-normal drop-shadow-sm">
            Hundens trivsel og tryghed kommer altid først. Rolig pelspleje i trygge rammer.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2 sm:pt-3">
            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#dabeb9] px-6 sm:px-7 py-3 sm:py-3.5 text-xs sm:text-sm font-medium tracking-wide text-black shadow-md transition-all hover:bg-[#ebd2cd] hover:scale-[1.02] active:scale-98 whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check text-black text-xs" aria-hidden="true" />
              <span>Book tid</span>
            </a>

            <Link
              to="/ydelser-priser"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white bg-transparent px-5 sm:px-6 py-3 sm:py-3.5 text-xs sm:text-sm font-medium tracking-wide text-white transition-all hover:bg-white hover:text-black hover:border-white active:scale-98 whitespace-nowrap shadow-xs"
            >
              <span>Ydelser & priser</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>

            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/80 bg-black/20 backdrop-blur-xs px-5 sm:px-6 py-3 sm:py-3.5 text-xs sm:text-sm font-medium tracking-wide text-white transition-all hover:bg-white hover:text-black hover:border-white active:scale-98 whitespace-nowrap shadow-xs"
            >
              <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
              <span>Kontakt</span>
            </Link>
          </div>
        </div>

        {/* Wave divider on hero flowing into next section */}
        <WaveDivider fill="#FEFAF3" variant={1} className="absolute bottom-0 left-0 right-0" />
      </section>

      {/* KÆRLIGHED TIL HUNDEN (CORE BRAND PHILOSOPHY) */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#FEFAF3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium text-black tracking-tight leading-snug">
              Kundens ønsker er vigtige, men hensynet til hunden vægtes altid højst
            </h2>
          </div>

          {/* Centered value points - no boxes, clean, elegant and concise */}
          <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-3 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center px-4">
              <i className="fa-solid fa-hand-holding-heart text-3xl sm:text-4xl text-[#dabeb9] mb-4" aria-hidden="true" />
              <h3 className="font-heading text-lg sm:text-xl font-medium text-black tracking-tight">
                Omsorg & tryghed
              </h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed max-w-xs font-normal">
                Rolig tilgang og god tid, så din hund altid føler sig tryg.
              </p>
            </div>

            <div className="flex flex-col items-center text-center px-4">
              <i className="fa-solid fa-leaf text-3xl sm:text-4xl text-[#dabeb9] mb-4" aria-hidden="true" />
              <h3 className="font-heading text-lg sm:text-xl font-medium text-black tracking-tight">
                Økologiske produkter
              </h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed max-w-xs font-normal">
                Vi bruger økologiske plejeprodukter tilpasset hunden – til gavn for både hunde og naturen.
              </p>
            </div>

            <div className="flex flex-col items-center text-center px-4">
              <i className="fa-solid fa-tags text-3xl sm:text-4xl text-[#dabeb9] mb-4" aria-hidden="true" />
              <h3 className="font-heading text-lg sm:text-xl font-medium text-black tracking-tight">
                Attraktive & skarpe priser
              </h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed max-w-xs font-normal">
                Klare og ærlige priser uden ubehagelige overraskelser.
              </p>
            </div>
          </div>

          {/* Citat / H3 i skråskrift nederst i sektionen */}
          <div className="mt-14 sm:mt-16 text-center max-w-3xl mx-auto border-t border-[#E8DFD5]/70 pt-8 sm:pt-10">
            <h3 className="font-handwriting text-xl sm:text-2xl lg:text-3xl font-normal text-black/85 normal-case tracking-normal leading-relaxed">
              »Ro, tryghed og kærlig omsorg for din bedste ven«
            </h3>
          </div>
        </div>
      </section>

      {/* OM TEASER SEKTION - PLACERET UNDER KÆRLIGHED TIL HUNDEN */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white border-t border-neutral-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            {/* Left content block */}
            <div className="lg:col-span-6 order-1 space-y-6">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-black uppercase leading-[1.2] sm:leading-[1.16]">
                Velkommen til en salon bygget på nærvær, ro og ægte hundeglæde
              </h2>

              <p className="text-base sm:text-lg text-neutral-700 leading-relaxed font-normal">
                Mit navn er Trine. Jeg er uddannet dyrepasser og har arbejdet 8 år med dyr som fag – men har altid været omgivet af dyr hele mit liv. Efter 12 år i ambulancetjenesten, hvor jeg arbejdede med mennesker, er jeg vendt tilbage til dyrene og er nu uddannet hundefrisør. Det er arbejdet med hundene, der gør mig allermest glad i hverdagen.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9] px-7 py-3.5 text-sm font-medium tracking-wide text-black shadow-xs transition-all hover:bg-[#c9a7a0] active:scale-98"
                >
                  <span>Læs hele historien</span>
                  <i className="fa-solid fa-arrow-right text-xs text-black" aria-hidden="true" />
                </Link>

                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-6 py-3.5 text-sm font-medium tracking-wide text-black transition-colors hover:bg-black hover:text-white whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-current" aria-hidden="true" />
                  <span>Kontakt os</span>
                </Link>

                <a
                  href={`tel:${BUSINESS_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-transparent px-6 py-3.5 text-sm font-medium tracking-wide text-neutral-800 transition-colors hover:border-black hover:text-black whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-black" aria-hidden="true" />
                  <span className="whitespace-nowrap">{BUSINESS_INFO.phone}</span>
                </a>
              </div>
            </div>

            {/* Right side - asymmetric vertical masonry-style animated image reel */}
            <div className="lg:col-span-6 order-2">
              <div className="group relative h-[460px] sm:h-[520px] lg:h-[560px] overflow-hidden rounded-3xl p-1">
                {/* Asymmetric 2-column masonry grid */}
                <div className="grid grid-cols-2 gap-3.5 sm:gap-5 h-full">
                  {/* Column 1 - continuous scrolling reel */}
                  <div className="relative overflow-hidden">
                    <div className="flex flex-col gap-3.5 sm:gap-5 animate-reel-col-1 group-hover:[animation-play-state:paused]">
                      {reelCol1.map((item, idx) => (
                        <div
                          key={`reel-c1-${idx}`}
                          className={`relative ${item.aspect} shrink-0 overflow-hidden rounded-2xl sm:rounded-3xl shadow-sm ring-1 ring-neutral-200/70 bg-neutral-100`}
                        >
                          <img
                            src={getOptimizedImageUrl(item.url, { width: 600, quality: 85 })}
                            alt={item.alt}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Column 2 - continuous scrolling reel moving at different speed for layered parallax */}
                  <div className="relative overflow-hidden pt-8 sm:pt-12">
                    <div className="flex flex-col gap-3.5 sm:gap-5 animate-reel-col-2 group-hover:[animation-play-state:paused]">
                      {reelCol2.map((item, idx) => (
                        <div
                          key={`reel-c2-${idx}`}
                          className={`relative ${item.aspect} shrink-0 overflow-hidden rounded-2xl sm:rounded-3xl shadow-sm ring-1 ring-neutral-200/70 bg-neutral-100`}
                        >
                          <img
                            src={getOptimizedImageUrl(item.url, { width: 600, quality: 85 })}
                            alt={item.alt}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREMHÆVEDE YDELSER */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#FEFAF3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-black">
                Populære behandlinger
              </h2>
              <p className="mt-2 text-base text-neutral-700">
                Et udvalg af vores mest populære behandlinger i salonen i Fredericia.
              </p>
            </div>
          </div>

          {/* Grid of 3 key services - clean styling with less text */}
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {featuredServices.map((srv, idx) => (
              <div
                key={idx}
                className="flex flex-col justify-between rounded-2xl border border-neutral-200 bg-white p-6 sm:p-7 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-shadow duration-300"
              >
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl font-medium text-black">
                    {srv.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                    {srv.desc}
                  </p>

                  <div className="mt-5 pt-3.5 border-t border-neutral-100">
                    <span className="text-xl font-bold text-black font-heading">
                      {srv.price}
                    </span>
                    <span className="text-xs text-neutral-600 block mt-0.5">
                      Varighed: {srv.time}
                    </span>
                  </div>
                </div>

                <div className="mt-6 pt-2 flex flex-col gap-2.5">
                  <a
                    href={BUSINESS_INFO.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#dabeb9] px-5 py-2.5 text-xs font-medium tracking-wide text-black transition-colors hover:bg-[#c9a7a0]"
                  >
                    <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
                    <span>Book tid</span>
                  </a>
                  {/* Secondary CTA - softer border */}
                  <Link
                    to={srv.link}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-neutral-300 bg-transparent py-2 text-xs font-medium text-neutral-800 transition-colors hover:bg-transparent hover:text-black hover:border-neutral-400"
                  >
                    <span>Læs mere</span>
                    <i className="fa-solid fa-angle-right text-[10px] text-neutral-600" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Kontakt opfordring under populære behandlinger */}
          <div className="mt-12 rounded-3xl border border-[#E8DFD5] bg-white p-6 sm:p-8 text-center max-w-2xl mx-auto shadow-xs">
            <h3 className="font-heading text-lg sm:text-xl font-medium text-black">
              I tvivl om den rette behandling til din hund?
            </h3>
            <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
              Du er altid velkommen til at kontakte os med spørgsmål om pelspleje, raceklip eller din hunds specifikke behov.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9] px-6 py-2.5 text-xs sm:text-sm font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0]"
              >
                <i className="fa-solid fa-comment-dots text-black" aria-hidden="true" />
                <span>Kontakt os</span>
              </Link>
              <a
                href={`tel:${BUSINESS_INFO.phoneRaw}`}
                className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-5 py-2.5 text-xs sm:text-sm font-medium text-black transition-colors hover:bg-black hover:text-white"
              >
                <i className="fa-solid fa-phone text-current" aria-hidden="true" />
                <span>{BUSINESS_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ÅBNINGSTILBUD KAMPAGNESEKTIOM */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <CampaignBanner />
        </div>
      </section>

      {/* GALLERI PÅ FORSIDE */}
      <GallerySection
        title="Glimt fra Hundens Pels og Pleje"
        subtitle="Se vores tilfredse firbenede kunder og hyggelige stunder fra salonen i Fredericia."
        maxImages={8}
      />

      {/* ÅBNINGSTILBUD POP-UP */}
      <OpeningOfferModal />
    </>
  );
}
